import { IBusiness } from "@/business/business";

export const hasPayrollScriptAccess = (
  business?: Pick<
    IBusiness,
    "payroll_script_config" | "has_payroll_results"
  > | null,
) => !!business?.has_payroll_results || !!business?.payroll_script_config;

export const hasAiScriptAccess = (
  business?: Partial<Pick<IBusiness, "has_published_ai_script_version">> | null,
) => !!business?.has_published_ai_script_version;

export const hasIcmPayrollViewerAccess = (
  business?: Partial<Pick<IBusiness, "is_icm_enabled">> | null,
) => !!business?.is_icm_enabled;

/**
 * Whether this user may rerun the whole payroll. Someone assigned a single
 * department sheet may not. The shared rerun control limits them to their own
 * section.
 *
 * Three cases, deliberately not two:
 * - no business loaded yet → false, so a payroll-run control never appears before
 *   we know whose it is;
 * - business loaded but the field absent → true. This is deploy skew (web ahead of
 *   api), and the field's absence is not evidence of restriction. The endpoint
 *   enforces authority independently, so the cost of being wrong here is a control
 *   that returns 403 — against the cost of hiding payroll from everyone until api
 *   catches up;
 * - field present → obey it.
 */
export const canRerunFullPayroll = (business?: IBusiness | null) =>
  !!business && business.can_rerun_full_payroll !== false;

/**
 * Whether this user may start a rerun that rewrites the WHOLE workbook.
 *
 * The rule lives on the API (`Payroll::WholeWorkbookRunAuthority`), because it
 * moves with the scoped-rerun rollout and the run endpoint admits on it. Reading
 * the rollout flag here instead meant guessing: `useFeatureFlag` reports an
 * absent flag as enabled in development and disabled elsewhere, so a fresh local
 * database hid every rerun control from a department lead, and a slow or failed
 * flag fetch in production offered one the endpoint would refuse.
 *
 * `can_run_whole_payroll` is the endpoint's answer minus one term: for a period
 * that already has a workbook the endpoint also requires an editable sheet IN it,
 * which no workbook-independent field can report. `hasEditableSheet` supplies
 * that from the workbook already loaded, so the two agree by construction.
 *
 * Absent field → fall back to `can_rerun_full_payroll`, which is the pre-rollout
 * rule and the same deploy-skew reasoning as that field's own.
 */
export const canRerunWholeWorkbook = ({
  business,
  hasEditableSheet = false,
}: {
  business?: IBusiness | null;
  /** Whether any sheet in the loaded workbook is editable by this user. */
  hasEditableSheet?: boolean;
}) => {
  if (!business) return false;
  if (business.can_run_whole_payroll === undefined) {
    return canRerunFullPayroll(business);
  }
  if (!business.can_run_whole_payroll) return false;

  // Full-workbook authority short-circuits so the control does not flicker off
  // while the workbook loads for the users who plainly have it.
  return canRerunFullPayroll(business) || hasEditableSheet;
};

export const canRunInitialPayroll = (
  business: IBusiness | null | undefined,
  completedWorkbookId: string | null,
) => !!business?.can_run_initial_payroll && !completedWorkbookId;

export const isPayrollBusiness = (business?: IBusiness | null) =>
  hasPayrollScriptAccess(business) ||
  hasAiScriptAccess(business) ||
  hasIcmPayrollViewerAccess(business);
